import React, { useState, useEffect } from "react";
import { TiInfo } from "react-icons/ti";
import axios from "axios";
import { BaseUrl } from "../App";
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';
const GeneralSetting = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [settings, setSettings] = useState({});
  const [successMessage, setSuccessMessage] = useState("")
  const [loading, setLoading] = useState(false);
  const inputStyles =
    "w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 outline-none hover:border-blue-300";
  const labelStyles = "block text-sm font-semibold text-gray-700 mb-2";
  const sectionStyles =
    "p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200";
  const gridStyles = "grid gap-6 sm:grid-cols-2 lg:grid-cols-3";
  const buttonBase =
    "px-6 py-2.5 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2";
  const buttonPrimary = `${buttonBase} bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 focus:ring-blue-500`;
  const cardStyles =
    "bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300";

  const [changedSettings, setChangedSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);
  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/settings.php`,
        {
          action: "get_all_settings",
          api_key: "1",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "true" && response.data.statuscode === 200) {
        const fetchedSettings = response.data.response.reduce((acc, item) => {
          acc[item.option_name] =
            item.option_name === "ct_postal_code"
              ? item.postalcode
              : item.option_value;
          return acc;
        }, {});

        setSettings((prevSettings) => ({
          ...prevSettings,
          ...fetchedSettings,
        }));
      } else {
        throw new Error(response.data.response || "Failed to fetch settings");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleChange = (key) => {
    const newValue = settings[key] === "Y" ? "N" : "Y";
    setSettings((prev) => ({ ...prev, [key]: newValue }));
    setChangedSettings((prev) => ({ ...prev, [key]: newValue }));
  };

  const handleInputChange = (key, value) => {
    let storedValue = value;
    if (
      [
        "ct_time_interval",
        "ct_additional_slot_time",
        "ct_min_advance_booking_time",
        "ct_max_advance_booking_time",
        "ct_cancellation_buffer_time",
        "ct_reshedule_buffer_time",
      ].includes(key)
    ) {
      storedValue = convertToMinutes(value);
    }
    setSettings((prev) => ({ ...prev, [key]: value }));
    setChangedSettings((prev) => ({
      ...prev,
      [key]: key === "ct_postal_code" ? { postalcode: storedValue } : storedValue,
    }));
  };

  const handlePostalCodeChange = (e) => {
    const postalCodes = e.target.value
      .split(",")
      .map((code) => code.trim())
      .join(",");
    handleInputChange("ct_postal_code", postalCodes);
  };

  const convertToMinutes = (value) => {
    if (typeof value !== "string") return value;
    const [amount, unit] = value.toLowerCase().split(" ");
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return value;
    switch (unit) {
      case "minutes":
      case "minute":
        return numAmount;
      case "hours":
      case "hour":
        return numAmount * 60;
      case "days":
      case "day":
        return numAmount * 24 * 60;
      case "months":
      case "month":
        return numAmount * 30 * 24 * 60;
      case "years":
      case "year":
        return numAmount * 365 * 24 * 60;
      default:
        return value;
    }
  };

  const convertFromMinutes = (minutes, options) => {
    if (minutes === undefined || minutes === null || isNaN(parseInt(minutes)))
      return options[0];
    minutes = parseInt(minutes);
    const optionValues = options.map((option) => convertToMinutes(option));
    const closestValue = optionValues.reduce((prev, curr) =>
      Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev
    );
    return options[optionValues.indexOf(closestValue)];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUpdateStatus(null);

    for (const [key, value] of Object.entries(changedSettings)) {
      try {
        const payload = {
          action: "update_setting",
          api_key: "1",
          option_name: key,
        };

        if (key === "ct_postal_code") {
          payload.postalcode = value.postalcode;
        } else {
          payload.option_value = value;
        }

        const response = await axios.post(
          `${BaseUrl}/backend/api/settings.php`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (
          response.data.status !== "true" ||
          response.data.statuscode !== 200
        ) {
          throw new Error(`Failed to update setting: ${key}`);
        }
      } catch (error) {
        setError(error.message);
        setUpdateStatus("error");
        setLoading(false);
        return;
      }
    }

    setSuccessMessage("Updated Setting Successfully");
    setUpdateStatus("success");
    setChangedSettings({});
    setLoading(false);
  };

  const Dropdown = ({ label, options, value, onChange, icon }) => (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <span className="flex items-center gap-2">
          {label}
          {icon}
        </span>
      </label>
      <select
        className="w-full p-2 border rounded-md bg-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  // Updated Toggle component with consistent spacing
  const Toggle = ({ label, checked, onChange, icon }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 w-full">
      <div className="flex items-start sm:items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700 break-words max-w-[calc(100%-2rem)]">
          {label}
        </span>
        {icon && <span className="mt-0.5 sm:mt-0">{icon}</span>}
      </div>
      <label className="relative inline-block w-11 h-6 shrink-0">
        <input
          type="checkbox"
          checked={checked === "Y"}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`
                    absolute inset-0
                    rounded-full 
                    transition-colors duration-200 ease-in-out
                    cursor-pointer
                    ${checked === "Y" ? "bg-green-500" : "bg-gray-300"}
                `}
        >
          <span
            className={`
                        absolute left-1 top-1
                        w-4 h-4
                        bg-white rounded-full
                        shadow-sm
                        transition-transform duration-200 ease-in-out
                        ${checked === "Y" ? "translate-x-5" : "translate-x-0"}
                    `}
          />
        </div>
      </label>
    </div>
  );

  // if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
    <div className="min-h-screen bg-gray-50">
          {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Add consistent padding for mobile */}
      <div className="w-full mx-auto px-4 py-6 sm:px-6 lg:px-8 max-w-7xl">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md">
          {/* Header with consistent padding */}
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              General Settings
            </h1>
          </div>
          <div className="flex justify-end pt-6 pr-4">
            <button
              type="submit"
              className={buttonPrimary}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                "Save Settings"
              )}
            </button>
          </div>

          <div className="space-y-8 p-6">
            {/* Postal Code Section */}
            <section className={sectionStyles}>
              <div className="space-y-4">
                <Toggle
                  label="Postal Code"
                  icon={<TiInfo className="text-gray-500 ml-2" />}
                  checked={settings.ct_postalcode_status}
                  onChange={() => handleToggleChange("ct_postalcode_status")}
                />

                  <div className="mt-4">
                    <label className={labelStyles}>Enter Postal Codes</label>
                    <textarea
                      value={settings.ct_postal_code || ""}
                      onChange={handlePostalCodeChange}
                      placeholder="e.g., 394107,395001"
                      className={`${inputStyles} min-h-[100px]`}
                      rows={4}
                    />
                  </div>
              </div>
            </section>

            {/* Time Settings Section */}
            <section className={sectionStyles}>
              <h2 className="text-lg font-semibold mb-6">Time Settings</h2>
              <div className={gridStyles}>
                <Dropdown
                  label="Time Interval"
                  options={[
                    "10 Minutes",
                    "15 Minutes",
                    "20 Minutes",
                    "30 Minutes",
                    "45 Minutes",
                    "1 Hours",
                    "1.5 Hours",
                    "2 Hours",
                    "2.5 Hours",
                    "3 Hours",
                  ]}
                  value={convertFromMinutes(settings.ct_time_interval, [
                    "10 Minutes",
                    "15 Minutes",
                    "20 Minutes",
                    "30 Minutes",
                    "45 Minutes",
                    "1 Hours",
                    "1.5 Hours",
                    "2 Hours",
                    "2.5 Hours",
                    "3 Hours",
                  ])}
                  onChange={(value) =>
                    handleInputChange("ct_time_interval", value)
                  }
                  icon={<TiInfo className="text-gray-500 ml-2" />}
                />

                <Dropdown
                  label="Additional Slot Time"
                  options={["30 Minutes", "1 Hours", "2 Hours"]}
                  value={convertFromMinutes(settings.ct_additional_slot_time, [
                    "30 Minutes",
                    "1 Hours",
                    "2 Hours",
                  ])}
                  onChange={(value) =>
                    handleInputChange("ct_additional_slot_time", value)
                  }
                />

                <Dropdown
                  label="Minimum Advance Booking"
                  options={[
                    "10 Minutes",
                    "20 Minutes",
                    "30 Minutes",
                    "40 Minutes",
                    "1 Hours",
                  ]}
                  value={convertFromMinutes(
                    settings.ct_min_advance_booking_time,
                    [
                      "10 Minutes",
                      "20 Minutes",
                      "30 Minutes",
                      "40 Minutes",
                      "1 Hours",
                    ]
                  )}
                  onChange={(value) =>
                    handleInputChange("ct_min_advance_booking_time", value)
                  }
                />
              </div>
            </section>

            {/* Currency Section */}
            <section className={sectionStyles}>
              <h2 className="text-lg font-semibold mb-6">Currency Settings</h2>
              <div className={gridStyles}>
                <Dropdown
                  label="Currency"
                  options={["USD", "EUR", "GBP", "INR"]}
                  value={settings.ct_currency}
                  onChange={(value) => handleInputChange("ct_currency", value)}
                />

                <Dropdown
                  label="Price Format"
                  options={[
                    "0 (e.g.$100)",
                    "1 (e.g.$100.0)",
                    "2 (e.g.$100.00)",
                  ]}
                  value={settings.ct_price_format_decimal_places}
                  onChange={(value) =>
                    handleInputChange("ct_price_format_decimal_places", value)
                  }
                />

                <Dropdown
                  label="Symbol Position"
                  options={["Before(e.g.$100)", "After(e.g.100$)"]}
                  value={settings.ct_currency_symbol_position}
                  onChange={(value) =>
                    handleInputChange("ct_currency_symbol_position", value)
                  }
                />
              </div>
            </section>

            {/* Tax Section */}
            <section className={sectionStyles}>
              <div className="space-y-6">
                <Toggle
                  label="Tax/VAT Settings"
                  checked={settings.ct_tax_vat_status}
                  onChange={() => handleToggleChange("ct_tax_vat_status")}
                />

                <div className="ml-6 space-y-4">
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="taxType"
                        value="F"
                        checked={settings.ct_tax_vat_type === "F"}
                        onChange={() =>
                          handleInputChange("ct_tax_vat_type", "F")
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>Flat Fee</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="taxType"
                        value="P"
                        checked={settings.ct_tax_vat_type === "P"}
                        onChange={() =>
                          handleInputChange("ct_tax_vat_type", "P")
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>Percentage</span>
                    </label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      className={inputStyles}
                      value={settings.ct_tax_vat_value}
                      onChange={(e) =>
                        handleInputChange("ct_tax_vat_value", e.target.value)
                      }
                      placeholder="Enter tax value"
                    />
                    {settings.ct_tax_vat_type === "P" && (
                      <span className="text-lg font-medium">%</span>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Additional Settings */}
            <section className={sectionStyles}>
              <h2 className="text-lg font-semibold mb-6">
                Additional Settings
              </h2>
              <div className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className={labelStyles}>Minimum Booking Price</label>
                    <input
                      type="number"
                      className={inputStyles}
                      value={settings.ct_minimum_booking_price}
                      onChange={(e) =>
                        handleInputChange(
                          "ct_minimum_booking_price",
                          e.target.value
                        )
                      }
                      placeholder="Enter minimum price"
                    />
                  </div>

                  <div>
                    <label className={labelStyles}>Thank You Page URL</label>
                    <input
                      type="url"
                      className={inputStyles}
                      value={settings.ct_thankyou_page_url}
                      onChange={(e) =>
                        handleInputChange(
                          "ct_thankyou_page_url",
                          e.target.value
                        )
                      }
                      placeholder="Enter URL"
                    />
                  </div>
                </div>

                <Toggle
                  label="Allow Multiple Bookings"
                  icon={<TiInfo className="text-gray-500 ml-2" />}
                  checked={
                    settings.ct_allow_multiple_booking_for_same_timeslot_status
                  }
                  onChange={() =>
                    handleToggleChange(
                      "ct_allow_multiple_booking_for_same_timeslot_status"
                    )
                  }
                />

                <Toggle
                  label="Auto Confirm Appointments"
                  icon={<TiInfo className="text-gray-500 ml-2" />}
                  checked={settings.ct_appointment_auto_confirm_status}
                  onChange={() =>
                    handleToggleChange("ct_appointment_auto_confirm_status")
                  }
                />
              </div>
            </section>

            {/* Save Button Section */}
            <div className="pt-6">
              <button
                type="submit"
                className={buttonPrimary}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Save Settings"
                )}
              </button>
            </div>

            {/* Status Messages */}
            {/* {updateStatus === "success" && (
              <div className="mt-4 p-4 bg-green-100 text-green-700 rounded-md">
                Settings updated successfully!
              </div>
            )}
            {updateStatus === "error" && (
              <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
                Failed to update settings. Please try again.
              </div>
            )} */}
          </div>
        </form>
      </div>
    </div>
   

  </div>
  );
};

export default GeneralSetting;
